import {
    ChangeEvent,
    Fragment,
    ReactElement,
    useCallback,
    useState,
} from "react";
import styled from "styled-components";
import { Logo } from "./Logo";
import { h } from "../../h";
import { Month } from "./Month";
import { Week } from "./Week";
import { GoToToday } from "./GoToToday";
import { LogIn } from "./LogIn";
import { Feedback } from "./Feedback";
import { useDispatch, useSelector } from "react-redux";
import { getIsCalendarOpen } from "../../state/isCalendarOpen.selectors";
import { getIsLogInOpen } from "../../state/isLogInOpen.selectors";
import {
    getIsAuthorized,
    getUser as getUserSelector,
} from "../../state/user.selectors";
import {
    logInToggle,
    userGet,
    userGetDone,
    userGetError,
    userLogOut,
} from "../../actions";
import {
    logInWithEmail,
    registerWithEmail,
    logOut,
    triggerSync,
} from "../../services/api";
import { User } from "../../model/user";
import { SyncStatusDot, syncIndicatorText } from "./SyncStatus";
import { getSyncIndicator } from "../../state/syncStatus.selectors";
import { ApiTokens } from "./ApiTokens";

const TopMenuContainerStyled = styled.div<{ isOpen?: boolean }>`
    z-index: 1;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    background-color: #eeeeee;
    overflow: hidden;
    transition: height 0.5s;
    height: ${({ isOpen }) => (isOpen ? `100%` : `46px`)};
`;

const TopMenuStyled = styled.div`
    display: flex;
    height: 46px;
`;

const TopMenuLeftStyled = styled.div`
    flex: 1 1 0;
`;

const TopMenuMiddleStyled = styled.div`
    flex: 0 0 500px;
    display: flex;
    align-items: stretch;
`;

const TopMenuRightStyled = styled.div`
    flex: 1 0 0;
    display: flex;
    justify-content: center;
`;

const TopMenuMiddleMonthStyled = styled.div`
    flex: 1 1 0;
`;

const TopMenuMiddleWeekStyled = styled.div`
    flex: 0 0 240px;
`;

const TopMenuMiddleTodayStyled = styled.div`
    flex: 1 1 0;
`;

const TopMenuRightItemStyled = styled.div`
    padding: 0 15px;
    display: flex;
    align-items: center;
`;

const LogInBodyStyled = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    height: calc(100vh - 46px);
`;

const LogInInputStyled = styled.input`
    width: 290px;
    height: 32px;
    margin: 10px 15px;
    outline: none;
    border: none;
    border-radius: 2px;
    padding-left: 11px;
    box-sizing: border-box;
    font-size: 18px;
    color: ${({ theme }) => theme.colorMain};
`;

const LogInButtonStyled = styled.button`
    cursor: pointer;
    background: none;
    outline: none;
    border: none;
    font-size: 18px;
    color: ${({ theme }) => theme.colorAccent};
    width: 290px;
    text-align: left;
    margin: 0 15px 0 0;
    padding: 6px 11px;

    &:hover {
        opacity: 0.6;
    }
`;

const LineStyled = styled.div`
    width: 100%;
    height: 1px;
    background-color: ${({ theme }) => theme.colorMain};
    margin: 30px 0;
    opacity: 0.5;
`;

const LogInLabelStyled = styled.div`
    font-size: 18px;
    color: ${({ theme }) => theme.colorMain};
    width: 290px;
    text-align: left;
    margin: 0 15px 0 0;
    padding: 6px 11px;
    box-sizing: border-box;
`;

const LogInErrorStyled = styled.div`
    font-size: 13px;
    color: ${({ theme }) => theme.colorWarning};
    width: 290px;
    text-align: left;
    margin: 0 15px 0 0;
    padding: 0 11px;
    box-sizing: border-box;
`;

const EmailAuth = (): ReactElement => {
    const dispatch = useDispatch();
    const [email, setEmail] = useState(``);
    const [password, setPassword] = useState(``);
    const [error, setError] = useState<string | null>(null);
    const [isPending, setIsPending] = useState(false);
    const submit = useCallback(
        async (
            authRequest: (email: string, password: string) => Promise<User>,
        ) => {
            setError(null);
            setIsPending(true);
            dispatch(userGet());
            try {
                const user = await authRequest(email, password);
                dispatch(userGetDone(user));
                dispatch(logInToggle());
            } catch (error) {
                setError(error.message);
                dispatch(userGetError(error.message));
            } finally {
                setIsPending(false);
            }
        },
        [dispatch, email, password],
    );
    return h(
        Fragment,
        {},
        h(LogInInputStyled, {
            placeholder: `Email...`,
            type: `email`,
            value: email,
            onChange: (event: ChangeEvent<HTMLInputElement>) =>
                setEmail(event.target.value),
        }),
        h(LogInInputStyled, {
            placeholder: `Password...`,
            type: `password`,
            value: password,
            onChange: (event: ChangeEvent<HTMLInputElement>) =>
                setPassword(event.target.value),
        }),
        error && h(LogInErrorStyled, {}, error),
        h(
            LogInButtonStyled,
            {
                disabled: isPending,
                onClick: () => submit(logInWithEmail),
            },
            `Log in`,
        ),
        h(
            LogInButtonStyled,
            {
                disabled: isPending,
                onClick: () => submit(registerWithEmail),
            },
            `Sign up`,
        ),
    );
};

const GoogleLogIn = (): ReactElement => {
    const dispatch = useDispatch();
    return h(
        LogInButtonStyled,
        {
            onClick: () => {
                (window as any).authorize = (user: User) => {
                    dispatch(userGetDone(user));
                    dispatch(logInToggle());
                };
                (window as any).authorizationFailed = () => {
                    dispatch(userGetError(`Google authorization failed`));
                };
                window.open(
                    `/auth/google-popup`,
                    `Login with Google`,
                    `width=400,height=500,menubar=0,toolbar=0,location=0,status=0`,
                );
            },
        },
        `Google`,
    );
};

const LoggedIn = (): ReactElement => {
    const dispatch = useDispatch();
    const user = useSelector(getUserSelector);
    const syncIndicator = useSelector(getSyncIndicator);
    return h(
        Fragment,
        {},
        h(
            LogInLabelStyled,
            {},
            `Logged in as ${(user && (user.username || user.provider)) || ``}`,
        ),
        h(LogInLabelStyled, {}, syncIndicatorText(syncIndicator)),
        h(
            LogInButtonStyled,
            {
                disabled: syncIndicator === `syncing`,
                onClick: () => triggerSync(),
            },
            `Sync now`,
        ),
        h(
            LogInButtonStyled,
            {
                onClick: async () => {
                    await logOut();
                    dispatch(userLogOut());
                    dispatch(logInToggle());
                },
            },
            `Log out`,
        ),
        h(LineStyled),
        h(ApiTokens),
    );
};

export const TopMenu = (): ReactElement => {
    const isCalendarOpen = useSelector(getIsCalendarOpen);
    const isLogInOpen = useSelector(getIsLogInOpen);
    const isAuthorized = useSelector(getIsAuthorized);
    if (isLogInOpen) {
        return h(
            TopMenuContainerStyled,
            { isOpen: true },
            h(
                TopMenuStyled,
                {},
                h(TopMenuLeftStyled, {}),
                h(
                    TopMenuMiddleStyled,
                    {},
                    h(TopMenuMiddleMonthStyled, {}),
                    h(TopMenuMiddleWeekStyled, {}),
                    h(TopMenuMiddleTodayStyled, {}),
                ),
                h(
                    TopMenuRightStyled,
                    {},
                    h(
                        TopMenuRightItemStyled,
                        {
                            style: {
                                width: 50,
                            },
                        },
                        h(LogIn),
                    ),
                    h(
                        TopMenuRightItemStyled,
                        {
                            style: {
                                visibility: `hidden`,
                            },
                        },
                        h(Feedback),
                    ),
                ),
            ),
            h(
                LogInBodyStyled,
                {},
                isAuthorized
                    ? h(LoggedIn)
                    : h(
                          Fragment,
                          {},
                          h(EmailAuth),
                          h(LineStyled),
                          h(LogInLabelStyled, {}, `Log in with:`),
                          h(GoogleLogIn),
                          h(LineStyled),
                      ),
            ),
        );
    }
    if (isCalendarOpen) {
        return h(
            TopMenuContainerStyled,
            { isOpen: true },
            h(
                TopMenuStyled,
                {},
                h(TopMenuLeftStyled, {}),
                h(
                    TopMenuMiddleStyled,
                    {},
                    h(TopMenuMiddleMonthStyled, {}, h(Month)),
                    h(TopMenuMiddleWeekStyled, {}, h(Week)),
                    h(TopMenuMiddleTodayStyled, {}, h(GoToToday)),
                ),
                h(TopMenuRightStyled, {}),
            ),
        );
    }
    return h(
        TopMenuContainerStyled,
        {},
        h(
            TopMenuStyled,
            {},
            h(TopMenuLeftStyled, {}, h(Logo)),
            h(
                TopMenuMiddleStyled,
                {},
                h(TopMenuMiddleMonthStyled, {}, h(Month)),
                h(TopMenuMiddleWeekStyled, {}, h(Week)),
                h(TopMenuMiddleTodayStyled, {}, h(GoToToday)),
            ),
            h(
                TopMenuRightStyled,
                {},
                h(TopMenuRightItemStyled, {}, h(SyncStatusDot)),
                h(
                    TopMenuRightItemStyled,
                    {
                        style: {
                            width: 50,
                        },
                    },
                    h(LogIn),
                ),
                h(TopMenuRightItemStyled, {}, h(Feedback)),
            ),
        ),
    );
};
